/**
 * WHICH APPLICATION IS THIS? — when the answer is the address, not the path.
 *
 * On the platform's own host a URL says what it means: `/run/{tenant}/{project}`
 * names the application, and the shell is the builder. On a project's OWN
 * domain (erp.acme.com) there is no such path — the whole host is one
 * application — so the server writes the answer into the document it serves:
 *
 *     <script>window.__TWASTA_HOST__ = {...}</script>
 *
 * It is delivered in the HTML rather than fetched because the alternative is
 * an empty shell (or worse, the builder's own first paint) on somebody's ERP
 * while a request goes out to ask. Everything that needs the answer reads it
 * from here, synchronously, before the first render.
 *
 * The split dev server has nothing injecting into its index.html, so
 * `loadHostBinding()` fills the gap there by asking the backend once at boot.
 * That path is DEV-only on purpose: in production a missing binding means
 * this is the platform, and a request per page load to re-confirm it would be
 * a cost paid by every user of the builder.
 */

export interface HostBinding {
  /** The hostname this shell is being served at. */
  hostname: string
  /** Organization that owns the application. */
  tenant_id: string
  /** Platform id of the account whose folder holds it — what every
   *  project-scoped endpoint is keyed by. */
  owner_user_id: string
  /** Application (project) name. */
  project: string
  /** Which face of the project this host serves. */
  target: 'app' | 'site'
  /** Which published site, when `target` is 'site'. */
  site_slug: string | null
  /** The platform's own address — the only way back to Twasta from here. */
  platform_url: string
  /** This address's row id. A sign-in names the destination by ID so the
   *  redirect target is looked up rather than supplied — see `api/sso`. */
  domain_id: number
  /** `{site_slug: origin}` for each of the project's websites that answers
   *  on a domain of its own (app-bound hosts only). */
  site_origins: Record<string, string>
}

declare global {
  interface Window {
    __TWASTA_HOST__?: Partial<HostBinding> | null
  }
}

let cached: HostBinding | null | undefined

function read(): HostBinding | null {
  const raw = typeof window !== 'undefined' ? window.__TWASTA_HOST__ : null
  if (!raw || typeof raw !== 'object') return null
  // A binding missing either half of the identity is not usable, and half-
  // applying it would render the builder against somebody's domain. Treat it
  // as absent.
  if (!raw.project || !raw.owner_user_id) return null
  return {
    hostname: String(raw.hostname || window.location.hostname),
    tenant_id: String(raw.tenant_id || '_'),
    owner_user_id: String(raw.owner_user_id),
    project: String(raw.project),
    target: raw.target === 'site' ? 'site' : 'app',
    site_slug: raw.site_slug ? String(raw.site_slug) : null,
    platform_url: String(raw.platform_url || ''),
    domain_id: Number(raw.domain_id || 0),
    site_origins: raw.site_origins && typeof raw.site_origins === 'object'
      ? { ...raw.site_origins } : {},
  }
}

/** The application this host serves, or null on the platform's own host. */
export function getHostBinding(): HostBinding | null {
  if (cached === undefined) cached = read()
  return cached
}

/** True when this shell is one application at its own address. */
export function isHostBound(): boolean {
  return getHostBinding() !== null
}

/**
 * Ask the backend which application this host is, for the split dev server
 * where nothing has been injected into index.html. Resolves to the binding or
 * null; never throws, and never runs in a production build.
 */
export async function loadHostBinding(): Promise<HostBinding | null> {
  if (getHostBinding()) return getHostBinding()
  if (!import.meta.env.DEV) return null
  try {
    const res = await fetch('/api/runapp/host', { credentials: 'include' })
    if (!res.ok) return null
    const data = await res.json()
    window.__TWASTA_HOST__ = data
    cached = undefined
    return getHostBinding()
  } catch {
    // The platform's host answers 404 here, and so does an offline backend.
    // Either way this is not a project domain.
    return null
  }
}

/**
 * The address of one of a project's websites — what the Run view links to
 * and frames when it shows the site.
 *
 * On the platform that is its own `/site/...` viewer. On a project's app
 * domain `/site/...` is no route (the whole host is the application), so the
 * link has to leave: for the site's own domain when it has one, else for the
 * platform's viewer, which serves an external site to anyone.
 */
export function siteAddress(ownerUserId: string, project: string, siteSlug: string): string {
  const viewer = `/site/${encodeURIComponent(ownerUserId)}/${encodeURIComponent(project)}/${encodeURIComponent(siteSlug)}`
  const binding = getHostBinding()
  if (!binding) return viewer
  const own = binding.site_origins[siteSlug]
  if (own) return own.replace(/\/+$/, '')
  return `${binding.platform_url.replace(/\/+$/, '')}${viewer}`
}
